import { useFlag } from '@unleash/proxy-client-react';
import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';
import type { CooldownData } from '../businessLogicService/brainBreakCooldown';
import { checkCooldownEligibility } from '../businessLogicService/brainBreakCooldown';
import { BrainBreakModal } from '../components/BrainBreak/BrainBreakModal';
import { getExperimentSpeculativeData } from '../graphQLOperations/Experiments';
import type {
  ExperimentsGetSpeculativeDataQuery,
  ExperimentsGetSpeculativeDataQuery$data,
} from '../graphQLOperations/__generated__/ExperimentsGetSpeculativeDataQuery.graphql';
import {
  BRAIN_BREAK_FLAG_NAME,
  COOLDOWN_VARIANT_NAME,
  BrainBreakActions,
  BrainBreakState,
  type BrainBreakPlacement,
} from '../models/BrainBreak';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  hydrateBrainBreakSession,
  setAssignedPlacement,
  markBrainBreakViewed,
  resetBrainBreakSession,
  showBrainBreakModal,
  closeBrainBreakModal,
  selectBrainBreakSession,
  selectBrainBreakModal,
} from '../redux/reducers/brainBreakSlice';
import { selectUser } from '../redux/reducers/userSlice';
import { useBrainBreakSessionLock } from '../custom-hooks/useBrainBreakSessionLock';
import { format } from 'date-fns';
import { getAssignedPlacement, isAssignedPlacement } from '../businessLogicService/brainBreakPlacementHash';
import { getDateByUserTimezone } from '../businessLogicService/getDateByUserTimezone';
import User, { Roles } from '../models/User';
interface BrainBreakProviderProps {
  children: ReactNode;
}

interface BrainBreakProviderInnerProps extends BrainBreakProviderProps {
  userId: string | undefined;
}

interface SpeculativeDataItem {
  readonly id: string;
  readonly flagName: string | null | undefined;
  readonly variantName: string | null | undefined;
  readonly modifiedOn: unknown;
  readonly payload:
    | {
        readonly rootElement: unknown;
      }
    | null
    | undefined;
}

export function extractCooldownData(
  query: ExperimentsGetSpeculativeDataQuery$data | null | undefined,
): CooldownData | undefined {
  const speculativeData = query?.userByPublicUserId?.experimentsSpeculativeData;
  const brainBreakData = speculativeData?.find(
    (data): data is SpeculativeDataItem => data?.variantName === COOLDOWN_VARIANT_NAME && !!data?.payload?.rootElement,
  );

  if (!brainBreakData?.payload?.rootElement) return undefined;

  const root = brainBreakData.payload.rootElement;
  let payload: CooldownData;

  try {
    payload = typeof root === 'string' ? JSON.parse(root) : root;
  } catch {
    return undefined;
  }

  if (!payload.lastCompletionDate) return undefined;

  return {
    lastCompletionDate: payload.lastCompletionDate,
  };
}

// Role gate for Brain Break: only student accounts are eligible. Exported so it
// can be unit-tested in isolation (see brainBreakContext.test.tsx), mirroring
// how extractCooldownData above is exported purely for testability.
export function isBrainBreakRoleEligible(user: User | null): boolean {
  return user?.roles?.some(userRole => userRole?.name === Roles.STUDENT) ?? false;
}

const BrainBreakDataLoader: FC<{
  queryRef: PreloadedQuery<ExperimentsGetSpeculativeDataQuery>;
  onHydrated: (cooldownData?: CooldownData, queryData?: ExperimentsGetSpeculativeDataQuery$data) => void;
}> = ({ queryRef, onHydrated }) => {
  const speculativeDataQuery = usePreloadedQuery(getExperimentSpeculativeData, queryRef);
  const hasHydrated = useRef(false);
  const onHydratedRef = useRef(onHydrated);
  onHydratedRef.current = onHydrated;

  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    try {
      const cooldownData = extractCooldownData(speculativeDataQuery);
      onHydratedRef.current(cooldownData, speculativeDataQuery);
    } catch {
      // Silently fail - cooldown data is optional
    }
  }, [speculativeDataQuery]);

  return null;
};

const BrainBreakStateContext = createContext<BrainBreakState | null>(null);
const BrainBreakActionsContext = createContext<BrainBreakActions | null>(null);

// Outer provider - tracks userId changes and forces remount via key
export const BrainBreakProvider: FC<BrainBreakProviderProps> = ({ children }) => {
  const user = useAppSelector(selectUser);
  const userId = user?.id?.toString();
  const previousUserIdRef = useRef<string | undefined>(undefined);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (previousUserIdRef.current && previousUserIdRef.current !== userId) {
      setKey(k => k + 1);
    }
    previousUserIdRef.current = userId;
  }, [userId]);

  return (
    <BrainBreakProviderInner key={key} userId={userId}>
      {children}
    </BrainBreakProviderInner>
  );
};

// Inner provider - remounts with fresh state when key changes
const BrainBreakProviderInner: FC<BrainBreakProviderInnerProps> = ({ children, userId }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { hasSeenBrainBreak, assignedPlacement, cooldownData, isHydrated } = useAppSelector(selectBrainBreakSession);
  const reduxModal = useAppSelector(selectBrainBreakModal);
  // Eligibility = kill-switch flag OFF *and* the account is a student.
  // The provider wraps the whole router, so this role gate is what keeps
  // Brain Break from surfacing on teacher/admin views.
  const isEligible = !useFlag(BRAIN_BREAK_FLAG_NAME) && isBrainBreakRoleEligible(user);

  // Session lock for cross-tab deduplication
  const { acquireLock, releaseLock, tryLocalLock } = useBrainBreakSessionLock();

  // Local refs for callbacks (non-serializable, can't go in Redux)
  const navigationCallbackRef = useRef<(() => void) | null>(null);
  const completionCallbackRef = useRef<(() => void) | null>(null);
  const [speculativeDataQueryRef, loadSpeculativeDataQuery] =
    useQueryLoader<ExperimentsGetSpeculativeDataQuery>(getExperimentSpeculativeData);

  // Reset Redux state on mount (fresh state for new user)
  useEffect(() => {
    dispatch(resetBrainBreakSession());
  }, [dispatch]);

  // Hydration effect - load GraphQL data. Gated on isEligible so ineligible
  // accounts (teachers/admins, or kill-switch on) never issue the speculative
  // data query — loadQuery fires the network request immediately, regardless
  // of whether anything ever consumes the ref. Teachers therefore never
  // hydrate (isHydrated stays false), which is safe: showBrainBreak already
  // requires isEligible, and nothing else acts on isHydrated.
  useEffect(() => {
    const publicUserId = user?.sub;
    if (isEligible && publicUserId && !speculativeDataQueryRef) {
      loadSpeculativeDataQuery({ userId: publicUserId }, { fetchPolicy: 'network-only' });
    }
  }, [isEligible, user?.sub, speculativeDataQueryRef, loadSpeculativeDataQuery]);

  // Hydration callback
  const handleHydrated = useCallback(
    (cooldownData?: CooldownData, _queryData?: ExperimentsGetSpeculativeDataQuery$data) => {
      const eligibility = checkCooldownEligibility(cooldownData);
      dispatch(
        hydrateBrainBreakSession({
          cooldownData,
          eligible: eligibility.eligible,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (userId && isHydrated && !assignedPlacement && !hasSeenBrainBreak) {
      const tz = user?.activeTimeZone;
      const dateStr = tz ? format(getDateByUserTimezone(tz).zonedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      const placement = getAssignedPlacement(userId, dateStr);
      dispatch(setAssignedPlacement(placement));
    }
  }, [userId, isHydrated, assignedPlacement, hasSeenBrainBreak, user?.activeTimeZone, dispatch]);

  // Refs for latest state — keeps showBrainBreak stable across Redux updates
  const isEligibleRef = useRef(isEligible);
  const hasSeenBrainBreakRef = useRef(hasSeenBrainBreak);
  const isHydratedRef = useRef(isHydrated);
  const userIdRef = useRef(userId);
  const assignedPlacementRef = useRef(assignedPlacement);

  useEffect(() => {
    isEligibleRef.current = isEligible;
    hasSeenBrainBreakRef.current = hasSeenBrainBreak;
    isHydratedRef.current = isHydrated;
    userIdRef.current = userId;
    assignedPlacementRef.current = assignedPlacement;
  }, [isEligible, hasSeenBrainBreak, isHydrated, userId, assignedPlacement]);

  // Actions - wrapped to maintain same API
  const showBrainBreak = useCallback(
    async (navigationCallback?: () => void, onComplete?: () => void, placement?: BrainBreakPlacement) => {
      const canShow =
        isHydratedRef.current &&
        isEligibleRef.current &&
        !hasSeenBrainBreakRef.current &&
        !!placement &&
        !!userIdRef.current &&
        !!assignedPlacementRef.current &&
        isAssignedPlacement(assignedPlacementRef.current, placement);

      if (!canShow) {
        navigationCallback?.();
        return;
      }

      // Fast synchronous check — blocks same-browser tabs instantly (read-only)
      if (!tryLocalLock()) {
        navigationCallback?.();
        return;
      }

      const acquired = await acquireLock();
      if (!acquired) {
        navigationCallback?.();
        return;
      }

      // Store callbacks in refs, dispatch to Redux
      navigationCallbackRef.current = navigationCallback || null;
      completionCallbackRef.current = onComplete || null;
      dispatch(showBrainBreakModal(placement));
    },
    [dispatch, acquireLock, tryLocalLock],
  );

  const markAsViewed = useCallback(() => {
    dispatch(markBrainBreakViewed());
    dispatch(closeBrainBreakModal());
    releaseLock();
    navigationCallbackRef.current = null;
    completionCallbackRef.current = null;
  }, [dispatch, releaseLock]);

  const closeWithAnimation = useCallback(() => {
    markAsViewed();
  }, [markAsViewed]);

  // Handle completion
  const handleCompletionFinished = useCallback(() => {
    navigationCallbackRef.current?.();
    completionCallbackRef.current?.();
    navigationCallbackRef.current = null;
    completionCallbackRef.current = null;
  }, []);

  // Build state object for context (maintains same shape)
  const state = useMemo<BrainBreakState>(
    () => ({
      isModalOpen: reduxModal.isOpen,
      currentPlacement: reduxModal.currentPlacement,
      hasSeenBrainBreak,
      isEligible,
      isHydrated,
      assignedPlacement,
      cooldownData,
      navigationCallback: navigationCallbackRef.current,
      animationPhase: 'idle',
    }),
    [
      reduxModal.isOpen,
      reduxModal.currentPlacement,
      hasSeenBrainBreak,
      isEligible,
      isHydrated,
      assignedPlacement,
      cooldownData,
    ],
  );

  const actions = useMemo<BrainBreakActions>(
    () => ({
      showBrainBreak,
      markAsViewed,
      closeWithAnimation,
    }),
    [showBrainBreak, markAsViewed, closeWithAnimation],
  );

  return (
    <BrainBreakStateContext.Provider value={state}>
      <BrainBreakActionsContext.Provider value={actions}>
        {children}
        {speculativeDataQueryRef && user?.sub && (
          <BrainBreakDataLoader queryRef={speculativeDataQueryRef} onHydrated={handleHydrated} />
        )}
        {reduxModal.isOpen && user?.id && (
          <BrainBreakModal
            isOpen={reduxModal.isOpen}
            currentPlacement={reduxModal.currentPlacement}
            onClose={closeWithAnimation}
            onComplete={handleCompletionFinished}
            userId={userId}
          />
        )}
      </BrainBreakActionsContext.Provider>
    </BrainBreakStateContext.Provider>
  );
};

// Hooks remain unchanged (same API)
export const useBrainBreakState = (): BrainBreakState => {
  const context = useContext(BrainBreakStateContext);
  if (!context) throw new Error('useBrainBreakState must be used within BrainBreakProvider');
  return context;
};

export const useBrainBreakActions = (): BrainBreakActions => {
  const context = useContext(BrainBreakActionsContext);
  if (!context) throw new Error('useBrainBreakActions must be used within BrainBreakProvider');
  return context;
};
